import React from 'react'
import { Loader, MailPlus } from 'lucide-react'
import { cn, formatDate } from '@/utils'
import type { OrgInvitation } from '@/types/organization'
import {
  InvitationStatusBadge,
  ORG_DANGER_BUTTON,
  ORG_LABEL,
  ORG_SURFACE,
  OrgEmptyState,
  RoleBadge,
} from './OrgPrimitives'

interface OrgInvitationsListProps {
  invitations: OrgInvitation[]
  isLoading: boolean
  /** id of the invitation currently being revoked. */
  pendingInviteId: string | null
  onRevoke: (invitation: OrgInvitation) => void
}

/**
 * Pending invitations. OWNER/ADMIN only — `GET .../invitations` 403s for a plain
 * MEMBER, so the caller gates rendering on `canManage`.
 *
 * Not split by breakpoint: the row is the same JSX either way and only the
 * direction changes, so this is CSS (`flex-col` → `sm:flex-row`).
 */
export const OrgInvitationsList: React.FC<OrgInvitationsListProps> = ({
  invitations,
  isLoading,
  pendingInviteId,
  onRevoke,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-8">
        <Loader className="animate-spin text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]" size={20} />
        <span className="font-plus-jakarta text-[14px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          Loading invitations...
        </span>
      </div>
    )
  }

  if (invitations.length === 0) {
    return (
      <OrgEmptyState
        icon={MailPlus}
        title="No pending invitations"
        description="Invitations you send will appear here until they are accepted."
        className="py-8"
      />
    )
  }

  return (
    <div className="space-y-3">
      {invitations.map((invitation) => (
        <div
          key={invitation.id}
          className={cn(
            ORG_SURFACE,
            'flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between',
          )}
        >
          <div className="min-w-0">
            <p className="truncate font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
              {invitation.email}
            </p>
            <p className={cn('mt-1', ORG_LABEL)}>
              Invited {formatDate(invitation.created_at)}
              {invitation.expires_at ? ` · expires ${formatDate(invitation.expires_at)}` : ''}
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <RoleBadge role={invitation.role} />
            <InvitationStatusBadge status={invitation.status} />
            <button
              type="button"
              onClick={() => onRevoke(invitation)}
              disabled={pendingInviteId === invitation.id}
              className={ORG_DANGER_BUTTON}
            >
              Revoke
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

export default OrgInvitationsList
